import { z } from "zod";
import { canonicalSha256, } from "./canonicalize.js";
import { consequenceClassSchema, protocolIdSchema, protocolVersionIdSchema, protocolVersionSchema, sha256DigestSchema, } from "./identifiers.js";
export const universalProtocolPolicySchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    ref: protocolIdSchema,
    version: protocolVersionIdSchema,
    canonicalJson: z
        .object({
        profile: z.literal("excellent.canonical-json.v1"),
        objectKeyOrder: z.literal("unicode-code-unit"),
        arrayOrder: z.literal("preserved"),
        negativeZero: z.literal("normalize-to-zero"),
        nonFiniteNumbers: z.literal("rejected"),
        undefinedObjectMembers: z.literal("omitted"),
        timestamps: z.literal("rfc3339-offset-required"),
    })
        .strict(),
    projection: z
        .object({
        unknown: z.literal("INCOMPLETE"),
        notRun: z.literal("INCOMPLETE"),
        mixedPassFail: z.literal("PARTIAL"),
    })
        .strict(),
    consequence: z
        .object({
        namedHumanAcceptanceRequired: z.array(consequenceClassSchema),
        workerMayAcceptOwnContract: z.literal(false),
        retrospectiveMayAuthorizeProspectiveAction: z.literal(false),
    })
        .strict(),
    isolation: z
        .object({
        minimumVerifierBoundary: z.literal("distinct_execution_boundary"),
        sharedMutableRootDisclosureRequired: z.literal(true),
    })
        .strict(),
    evidenceStorage: z
        .object({
        inlineMaximumBytes: z.number().int().positive(),
        largerArtifacts: z.literal("content-addressed-external-location"),
        secretContentInlineAllowed: z.literal(false),
    })
        .strict(),
    trustRoots: z
        .object({
        distribution: z.literal("tenant-scoped-out-of-band-endpoint"),
        retiredKeysVerifyHistoricalReceipts: z.literal(true),
        compromisedKeysTrusted: z.literal(false),
        privateKeysDisclosed: z.literal(false),
    })
        .strict(),
    retention: z
        .object({
        unit: z.literal("days"),
        public: z.number().int().positive(),
        tenant: z.number().int().positive(),
        restricted: z.number().int().positive(),
        secretRedactedMetadata: z.number().int().positive(),
        deletionRequiresExplicitTenantPolicy: z.literal(true),
    })
        .strict(),
})
    .strict();
export const UNIVERSAL_PROTOCOL_POLICY = universalProtocolPolicySchema.parse({
    protocolVersion: "excellent.universal-verification.v1",
    ref: "policy:excellent:universal-protocol-defaults",
    version: "1",
    canonicalJson: {
        profile: "excellent.canonical-json.v1",
        objectKeyOrder: "unicode-code-unit",
        arrayOrder: "preserved",
        negativeZero: "normalize-to-zero",
        nonFiniteNumbers: "rejected",
        undefinedObjectMembers: "omitted",
        timestamps: "rfc3339-offset-required",
    },
    projection: {
        unknown: "INCOMPLETE",
        notRun: "INCOMPLETE",
        mixedPassFail: "PARTIAL",
    },
    consequence: {
        namedHumanAcceptanceRequired: ["critical"],
        workerMayAcceptOwnContract: false,
        retrospectiveMayAuthorizeProspectiveAction: false,
    },
    isolation: {
        minimumVerifierBoundary: "distinct_execution_boundary",
        sharedMutableRootDisclosureRequired: true,
    },
    evidenceStorage: {
        inlineMaximumBytes: 1048576,
        largerArtifacts: "content-addressed-external-location",
        secretContentInlineAllowed: false,
    },
    trustRoots: {
        distribution: "tenant-scoped-out-of-band-endpoint",
        retiredKeysVerifyHistoricalReceipts: true,
        compromisedKeysTrusted: false,
        privateKeysDisclosed: false,
    },
    retention: {
        unit: "days",
        public: 365,
        tenant: 180,
        restricted: 90,
        secretRedactedMetadata: 365,
        deletionRequiresExplicitTenantPolicy: true,
    },
});
export function universalProtocolPolicyDigest(sha256) {
    return sha256DigestSchema.parse(canonicalSha256(UNIVERSAL_PROTOCOL_POLICY, sha256));
}
export function requiresNamedHumanAcceptance(consequence) {
    return UNIVERSAL_PROTOCOL_POLICY.consequence.namedHumanAcceptanceRequired.includes(consequence);
}
